/**
 * SavedPromptsPanel — the saved-prompt library, opened from the composer
 * (2026-09-17).
 *
 * ONE surface with three bodies — the list you pick from, the fill-in step a
 * template needs, and the editor — because a second overlay would mean a second
 * Escape owner, a second focus restore and a second thing to get wrong. It is
 * the `pendingAdd` move the mention dropdown already makes: the list gives way
 * to one question and comes back.
 *
 * PORTAL CONTRACT, all three parts load-bearing and two of them written in
 * production incidents:
 *   1. portaled to `document.body` — the chat pane carries `backdrop-filter`
 *      and `container-type`, either of which makes it the containing block for
 *      a `position: fixed` child, so an in-tree popup is trapped at ANY
 *      z-index;
 *   2. `BODY_PORTAL_Z_INDEX` from `styles/layers`, never a literal — this is a
 *      menu belonging to the chat, and `check-overlay-layers` takes its
 *      denominator from every `createPortal` site under `src/`;
 *   3. `data-prest0n-portal="true"` on BOTH the scrim and the surface, checked
 *      BY VALUE by `portalClickGuard` — `ThreadArchiveNoticeOverlay` shipped it
 *      as `data-prest0n-portal=""`, which that selector never matched, and its
 *      scrim closed the whole chat on every click.
 *
 * It takes the library as an API PROP rather than importing the hook, so the
 * chat-isolation suites can render it: anything reaching `firebase/auth` pulls
 * a module that touches global `fetch` at import time and takes the suite down
 * before a test runs (2026-09-11). The `RoutineManagerPanel` arrangement.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import type { ThreadListLang } from '../../types/threadCategories';
import { foldCategoryName } from '../../types/categoryNames';
import {
  MAX_BLANKS_PER_PROMPT,
  PROMPT_BODY_MAX,
  canEditSharedPrompt,
  hasUnpublishedChanges,
  parseBlanks,
  renderPrompt,
  suggestPromptTitle,
  type PromptProblem,
  type SavedPrompt,
  type SharedPrompt,
} from '../../types/savedPrompts';
import { savedPromptsCopy } from './savedPromptsCopy';
import type { SavedPromptsApi } from '../../hooks/useSavedPrompts';
import {
  PromptBlankChip,
  PromptBlankChipRow,
  PromptDriftNote,
  PromptDriftRow,
  PromptEditorScroll,
  PromptFieldLabel,
  PromptRow,
  PromptRowAction,
  PromptRowMeta,
  PromptRowPreview,
  PromptRowShell,
  PromptRowTitle,
  PromptTeamMark,
  PromptsFilterChip,
  PromptsFilterRow,
  PromptTextArea,
  PromptTextInput,
  PromptsBody,
  PromptsFooter,
  PromptsFooterButton,
  PromptsGroupHeading,
  PromptsHandle,
  PromptsHeader,
  PromptsHeaderButton,
  PromptsNote,
  PromptsScrim,
  PromptsSearchInput,
  PromptsSearchRow,
  PromptsSurface,
  PromptsTitle,
} from './SavedPrompts.styles';
import { dropUpPlacement, useDropUpViewport } from './dropUpPlacement';

export type PromptInsertMode = 'replace' | 'append';

/** What the anchored panel wants to be, and the narrowest it stays useful at. */
const PROMPTS_PANEL_WIDTH = 420;
const PROMPTS_PANEL_MIN_WIDTH = 260;

export interface SavedPromptsPanelProps {
  open: boolean;
  /** Bottom sheet on a phone, anchored drop-up everywhere else. */
  variant: 'sheet' | 'panel';
  /**
   * The trigger's rect. Panel variant only.
   *
   * Measured at open and re-measured on RESIZE by the caller — never on scroll,
   * where re-anchoring is jitter. Whatever arrives here is clamped against the
   * live viewport by `dropUpPlacement` on every render, so even a stale anchor
   * cannot put the panel off screen.
   */
  anchor?: { left: number; top: number } | null;
  api: SavedPromptsApi;
  lang: ThreadListLang;
  /** The live draft, for "save this draft" and to decide replace vs append. */
  draftText: string;
  onClose: () => void;
  onPick: (text: string, mode: PromptInsertMode) => void;
}

type Body = 'list' | 'fill' | 'edit';

/** Which shelf a row came from. The person's own, or the workspace's. */
type Shelf = 'mine' | 'team';

/** Which shelves the list is showing. A filter, never a mode. */
type StoreFilter = 'all' | Shelf;

/**
 * One row, from either shelf.
 *
 * A discriminated union rather than a flattened row, because the two are NOT
 * the same thing and every decision downstream turns on which one it is: whose
 * prompt it is, whether a use stamp is owed (only your own carries one — how
 * often a colleague reaches for a prompt is their business, and the shared
 * document deliberately has no place to put it), and which core an edit goes
 * to. Flattening them would make every one of those a guess.
 */
type Row =
  | { shelf: 'mine'; prompt: SavedPrompt }
  | { shelf: 'team'; prompt: SharedPrompt };

/** The fields both shelves have, which is everything the list and the fill step read. */
type AnyPrompt = SavedPrompt | SharedPrompt;

/** Search over what a person would actually remember: the name, then the words. */
function matches(prompt: AnyPrompt, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    prompt.title.toLowerCase().includes(q)
    || prompt.body.toLowerCase().includes(q)
    || prompt.category.toLowerCase().includes(q)
  );
}

export const SavedPromptsPanel: React.FC<SavedPromptsPanelProps> = ({
  open, variant, anchor, api, lang, draftText, onClose, onPick,
}) => {
  const copy = savedPromptsCopy(lang);
  const [body, setBody] = useState<Body>('list');
  const [query, setQuery] = useState('');
  const [store, setStore] = useState<StoreFilter>('all');
  const [active, setActive] = useState(0);
  const [target, setTarget] = useState<AnyPrompt | null>(null);
  /** Which shelf `target` came from — an edit goes to a different door per shelf. */
  const [targetShelf, setTargetShelf] = useState<Shelf>('mine');
  /** The SERVER's own refusal, verbatim. Never a sentence invented here. */
  const [workspaceNote, setWorkspaceNote] = useState<string | null>(null);
  /** An inline confirm, so its real limit can be said in a sentence. */
  const [confirmWorkspace, setConfirmWorkspace] = useState<'share' | 'unshare' | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState({ title: '', body: '', category: '' });
  const [problem, setProblem] = useState<PromptProblem | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const searchRef = useRef<HTMLInputElement | null>(null);
  const restoreFocusRef = useRef<Element | null>(null);

  useBodyScrollLock(open && variant === 'sheet');

  /**
   * The panel's own geometry, recomputed against the LIVE viewport on every
   * render and on every resize rather than frozen at open — `dropUpPlacement.ts`
   * carries the whole reason, and the owner report that produced it.
   *
   * `null` for the phone sheet, which spans the screen edge to edge and has no
   * anchor to be clamped against.
   */
  const viewport = useDropUpViewport(open && variant === 'panel');
  const placement = variant === 'panel' && anchor
    ? dropUpPlacement({
      anchor,
      viewport,
      preferredWidth: PROMPTS_PANEL_WIDTH,
      minWidth: PROMPTS_PANEL_MIN_WIDTH,
    })
    : null;

  const hasDraft = draftText.trim().length > 0;

  /**
   * The team shelf is OFFERED only where it is reachable — somebody has
   * published something, or this person could. A member with no team library
   * and no way to make one is shown no chips at all rather than a filter that
   * always yields nothing.
   */
  const teamReachable = api.team.length > 0 || api.canPublish || api.canCurate;
  const effectiveStore: StoreFilter = teamReachable ? store : 'mine';

  // Both shelves, filtered by the search and by the chips, grouped by category.
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    if (effectiveStore !== 'team') {
      for (const prompt of api.prompts) if (matches(prompt, query)) out.push({ shelf: 'mine', prompt });
    }
    if (effectiveStore !== 'mine') {
      // The workspace's own, by name: a shared prompt carries no per-person use
      // stamp to order by, and that absence is the design rather than a gap.
      const shared = [...api.team].sort((a, b) => a.title.localeCompare(b.title));
      for (const prompt of shared) if (matches(prompt, query)) out.push({ shelf: 'team', prompt });
    }
    return out;
  }, [api.prompts, api.team, query, effectiveStore]);

  const groups = useMemo(() => {
    const byKey = new Map<string, { label: string; rows: Row[] }>();
    for (const row of rows) {
      const key = foldCategoryName(row.prompt.category) || '￿';
      const label = row.prompt.category || copy.noCategory;
      const bucket = byKey.get(key) ?? { label, rows: [] };
      bucket.rows.push(row);
      byKey.set(key, bucket);
    }
    // Within a category the person's own come first — those are the ones they
    // reach for — then the workspace's, each already in its own order.
    for (const bucket of byKey.values()) {
      bucket.rows.sort((a, b) => (a.shelf === b.shelf ? 0 : a.shelf === 'mine' ? -1 : 1));
    }
    return [...byKey.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v);
  }, [rows, copy.noCategory]);
  const flat = useMemo(() => groups.flatMap((g) => g.rows), [groups]);

  /** Whether the viewer may change this shared prompt — the contract's own rule. */
  const mayCurate = useCallback((shared: SharedPrompt) => canEditSharedPrompt({
    viewerUid: api.viewerUid,
    authorUid: shared.origin.authorUid,
    hasCuratePermission: api.canCurate,
  }), [api.viewerUid, api.canCurate]);

  // Fresh every time it opens: a picker that remembers last week's search is a
  // picker you have to clear before you can use.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    setBody('list');
    setQuery('');
    setStore('all');
    setActive(0);
    setTarget(null);
    setTargetShelf('mine');
    setValues({});
    setProblem(null);
    setConfirmDelete(null);
    setWorkspaceNote(null);
    setConfirmWorkspace(null);
    const id = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  const close = useCallback(() => {
    onClose();
    const el = restoreFocusRef.current as HTMLElement | null;
    if (el && typeof el.focus === 'function') el.focus();
  }, [onClose]);

  /**
   * `usedId` is present ONLY for the person's own prompts. The use stamp is a
   * write to `users/{uid}/prompt_templates/{id}`, so passing a shared prompt's
   * id would aim it at a document that does not exist — and a shared prompt
   * carries no per-person usage by design.
   */
  const insert = useCallback((text: string, mode: PromptInsertMode, usedId?: string) => {
    if (usedId) api.markUsed(usedId);
    onPick(text, mode);
    close();
  }, [api, onPick, close]);

  /** Zero blanks must never cost an extra step — that is the common case. */
  const choose = useCallback((row: Row) => {
    const blanks = parseBlanks(row.prompt.body);
    if (blanks.length === 0) {
      insert(
        row.prompt.body,
        hasDraft ? 'append' : 'replace',
        row.shelf === 'mine' ? row.prompt.id : undefined,
      );
      return;
    }
    setTarget(row.prompt);
    setTargetShelf(row.shelf);
    setValues({});
    setBody('fill');
  }, [insert, hasDraft]);

  const openEditor = useCallback((row: Row | null, seedBody = '') => {
    setTarget(row?.prompt ?? null);
    setTargetShelf(row?.shelf ?? 'mine');
    setProblem(null);
    setWorkspaceNote(null);
    setConfirmWorkspace(null);
    setDraft(row
      ? { title: row.prompt.title, body: row.prompt.body, category: row.prompt.category }
      : { title: suggestPromptTitle(seedBody), body: seedBody, category: '' });
    setBody('edit');
  }, []);

  /**
   * One save, three doors, decided by what is being edited rather than by a
   * mode the surface remembers: a new prompt, one of yours, or the workspace's
   * copy — which goes through the core, because no browser may write it.
   */
  const save = useCallback(async () => {
    setBusy(true);
    let problemResult: PromptProblem | null = null;
    if (target && targetShelf === 'team') {
      const refusal = await api.updateShared(target.id, draft);
      setBusy(false);
      if (refusal) { setWorkspaceNote(refusal); return; }
    } else {
      problemResult = target ? await api.update(target.id, draft) : await api.create(draft);
      setBusy(false);
      if (problemResult) { setProblem(problemResult); return; }
    }
    setBody('list');
    setTarget(null);
  }, [api, draft, target, targetShelf]);

  /**
   * Publishing, pushing and withdrawing — each one the server's decision, and
   * its refusal is shown in the server's own words rather than translated into
   * a second sentence here that could drift out of step with it.
   */
  const runWorkspace = useCallback(async (
    action: () => Promise<string | null>,
    { closeAfter = false }: { closeAfter?: boolean } = {},
  ) => {
    setBusy(true);
    setWorkspaceNote(null);
    const refusal = await action();
    setBusy(false);
    setConfirmWorkspace(null);
    if (refusal) { setWorkspaceNote(refusal); return; }
    if (closeAfter) { setBody('list'); setTarget(null); }
  }, []);

  /** Keep a colleague's prompt as one of your own — a create, so it is instant. */
  const saveCopy = useCallback(async (shared: SharedPrompt) => {
    const problemResult = await api.saveCopy(shared);
    setWorkspaceNote(problemResult ? copy.saveFailed : copy.copySaved);
  }, [api, copy.saveFailed, copy.copySaved]);

  // Escape in the CAPTURE phase with stopPropagation, so the chat's own Escape
  // handling does not also fire and dismiss two layers with one key.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      event.preventDefault();
      if (body !== 'list') { setBody('list'); setProblem(null); return; }
      close();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, body, close]);

  const onListKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (body !== 'list' || flat.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => (i + 1) % flat.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => (i - 1 + flat.length) % flat.length);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(flat.length - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const picked = flat[Math.min(active, flat.length - 1)];
      if (picked) choose(picked);
    }
  }, [body, flat, active, choose]);

  /** The target NARROWED to the person's own shelf — the only one they may delete. */
  const mineTarget = targetShelf === 'mine' ? (target as SavedPrompt | null) : null;

  const blanks = useMemo(() => (target ? parseBlanks(target.body) : []), [target]);
  const draftBlanks = useMemo(() => parseBlanks(draft.body), [draft.body]);

  const problemLine = useMemo(() => {
    switch (problem) {
      case 'empty-title': return copy.titleRequired;
      case 'empty-body': return copy.bodyRequired;
      case 'body-too-long': return copy.bodyTooLong(PROMPT_BODY_MAX);
      case 'too-many-blanks': return copy.tooManyBlanks(MAX_BLANKS_PER_PROMPT);
      case 'library-full': return copy.libraryFull(api.max);
      default: return null;
    }
  }, [problem, copy, api.max]);

  if (!open || typeof document === 'undefined' || !document.body) return null;

  const surface = (
    <>
      <PromptsScrim
        $variant={variant}
        data-prest0n-portal="true"
        data-saved-prompts-scrim=""
        onMouseDown={close}
      />
      <PromptsSurface
        $variant={variant}
        $left={placement?.left}
        $bottom={placement?.bottom}
        $width={placement?.width}
        data-prest0n-portal="true"
        data-saved-prompts-panel=""
        role="dialog"
        aria-modal="true"
        aria-label={copy.openLibrary}
        onKeyDown={onListKeyDown}
      >
        {variant === 'sheet' && <PromptsHandle aria-hidden="true" />}

        <PromptsHeader>
          <PromptsTitle>
            {body === 'fill' && target ? copy.fillInHeading(target.title) : copy.heading}
          </PromptsTitle>
          {body === 'list' ? (
            <PromptsHeaderButton type="button" onClick={() => openEditor(null)} disabled={!api.hasRoom}>
              {copy.newPrompt}
            </PromptsHeaderButton>
          ) : (
            <PromptsHeaderButton type="button" onClick={() => { setBody('list'); setProblem(null); }}>
              {copy.back}
            </PromptsHeaderButton>
          )}
        </PromptsHeader>

        {body === 'list' && (
          <>
            <PromptsSearchRow>
              <PromptsSearchInput
                ref={searchRef}
                type="search"
                value={query}
                placeholder={copy.searchPlaceholder}
                aria-label={copy.searchPlaceholder}
                data-saved-prompts-search=""
                onChange={(e) => { setQuery(e.target.value); setActive(0); }}
              />
            </PromptsSearchRow>
            {teamReachable && (
              <PromptsFilterRow role="group" aria-label={copy.heading}>
                {(['all', 'mine', 'team'] as const).map((key) => (
                  <PromptsFilterChip
                    key={key}
                    type="button"
                    $on={effectiveStore === key}
                    aria-pressed={effectiveStore === key}
                    data-prompts-filter={key}
                    onClick={() => { setStore(key); setActive(0); }}
                  >
                    {key === 'all' ? copy.filterAll : key === 'mine' ? copy.filterMine : copy.filterTeam}
                  </PromptsFilterChip>
                ))}
              </PromptsFilterRow>
            )}
            <PromptsBody role="listbox" aria-label={copy.openLibrary}>
              {api.loading && <PromptsNote>{'…'}</PromptsNote>}
              {api.degraded && <PromptsNote $tone="error">{copy.loadFailed}</PromptsNote>}
              {!api.loading && api.prompts.length === 0 && effectiveStore !== 'team' && (
                <PromptsNote>{`${copy.empty} — ${copy.emptyHint}`}</PromptsNote>
              )}
              {!api.teamLoading && effectiveStore === 'team' && api.team.length === 0 && (
                <PromptsNote>{copy.teamEmpty}</PromptsNote>
              )}
              {!api.loading && flat.length === 0 && query.trim().length > 0 && (
                <PromptsNote>{copy.noMatches}</PromptsNote>
              )}
              {groups.map((group) => (
                <div key={group.label} role="group" aria-label={group.label}>
                  <PromptsGroupHeading>{group.label}</PromptsGroupHeading>
                  {group.rows.map((row) => {
                    const index = flat.indexOf(row);
                    const count = row.prompt.blanks.length;
                    const drifted = row.shelf === 'mine' && hasUnpublishedChanges(row.prompt);
                    // Whose row it is decides the trailing control: you open
                    // your own (and a colleague's, with curate); otherwise the
                    // one thing you can do is keep a copy.
                    const editable = row.shelf === 'mine' || mayCurate(row.prompt);
                    return (
                      <PromptRowShell
                        key={`${row.shelf}:${row.prompt.id}`}
                        $active={index === active}
                        onMouseEnter={() => setActive(index)}
                      >
                        <PromptRow
                          type="button"
                          role="option"
                          aria-selected={index === active}
                          $active={index === active}
                          data-saved-prompt-row={row.prompt.id}
                          data-saved-prompt-shelf={row.shelf}
                          onClick={() => choose(row)}
                        >
                          <PromptRowTitle>
                            {row.shelf === 'team' && <PromptTeamMark>{copy.teamMark}</PromptTeamMark>}
                            {row.prompt.title}
                          </PromptRowTitle>
                          <PromptRowPreview>{row.prompt.body}</PromptRowPreview>
                          <PromptRowMeta>
                            {[
                              count > 0 ? copy.blanksCount(count) : '',
                              row.shelf === 'team' ? copy.sharedBy(row.prompt.origin.authorName) : '',
                            ].filter(Boolean).join(' · ')}
                          </PromptRowMeta>
                          {drifted && (
                            <PromptDriftRow>
                              <PromptDriftNote data-prompt-drift="">{copy.driftNote}</PromptDriftNote>
                            </PromptDriftRow>
                          )}
                        </PromptRow>
                        <PromptRowAction
                          type="button"
                          data-saved-prompt-action={editable ? 'edit' : 'copy'}
                          aria-label={editable ? copy.editPromptAction : copy.saveCopyAction}
                          onClick={() => (editable
                            ? openEditor(row)
                            : void saveCopy(row.prompt as SharedPrompt))}
                        >
                          {editable ? copy.editPromptAction : copy.saveCopyAction}
                        </PromptRowAction>
                      </PromptRowShell>
                    );
                  })}
                </div>
              ))}
              {workspaceNote && body === 'list' && (
                <PromptsNote data-prompts-workspace-note="">{workspaceNote}</PromptsNote>
              )}
            </PromptsBody>
            <PromptsFooter>
              <PromptsFooterButton
                type="button"
                $primary
                disabled={!hasDraft || !api.hasRoom}
                data-saved-prompts-save-draft=""
                onClick={() => openEditor(null, draftText)}
              >
                {copy.saveDraftAsPrompt}
              </PromptsFooterButton>
              <PromptsFooterButton type="button" onClick={close}>{copy.done}</PromptsFooterButton>
            </PromptsFooter>
          </>
        )}

        {body === 'fill' && target && (
          <>
            <PromptEditorScroll data-prompt-fill="">
              <PromptsNote>{copy.fillInHint}</PromptsNote>
              {blanks.map((label, i) => (
                <div key={label}>
                  <PromptFieldLabel htmlFor={`prompt-blank-${i}`}>{label}</PromptFieldLabel>
                  <PromptTextInput
                    id={`prompt-blank-${i}`}
                    autoFocus={i === 0}
                    value={values[label] ?? ''}
                    data-prompt-blank={label}
                    onChange={(e) => setValues((v) => ({ ...v, [label]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      e.preventDefault();
                      const next = document.getElementById(`prompt-blank-${i + 1}`) as HTMLInputElement | null;
                      if (next) next.focus();
                      else insert(renderPrompt(target.body, values), hasDraft ? 'append' : 'replace', target.id);
                    }}
                  />
                </div>
              ))}
            </PromptEditorScroll>
            <PromptsFooter>
              <PromptsFooterButton
                type="button"
                $primary
                data-prompt-fill-insert=""
                onClick={() => insert(renderPrompt(target.body, values), hasDraft ? 'append' : 'replace', target.id)}
              >
                {hasDraft ? copy.addToEnd : copy.insert}
              </PromptsFooterButton>
              {hasDraft && (
                <PromptsFooterButton
                  type="button"
                  onClick={() => insert(renderPrompt(target.body, values), 'replace', target.id)}
                >
                  {copy.replaceDraft}
                </PromptsFooterButton>
              )}
            </PromptsFooter>
          </>
        )}

        {body === 'edit' && (
          <>
            <PromptEditorScroll>
              <PromptFieldLabel htmlFor="prompt-title">{copy.nameLabel}</PromptFieldLabel>
              <PromptTextInput
                id="prompt-title"
                autoFocus
                value={draft.title}
                placeholder={copy.namePlaceholder}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              />
              <PromptFieldLabel htmlFor="prompt-body">{copy.bodyLabel}</PromptFieldLabel>
              <PromptTextArea
                id="prompt-body"
                value={draft.body}
                placeholder={copy.bodyPlaceholder}
                onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
              />
              {/* The live blank chips ARE the documentation for {{ }} — nobody
                  reads a syntax note, and everybody reads their own words. */}
              <PromptBlankChipRow>
                {draftBlanks.length === 0
                  ? <PromptsNote>{copy.blanksNone}</PromptsNote>
                  : draftBlanks.map((b) => <PromptBlankChip key={b}>{b}</PromptBlankChip>)}
              </PromptBlankChipRow>
              <PromptFieldLabel htmlFor="prompt-category">{copy.categoryLabel}</PromptFieldLabel>
              <PromptTextInput
                id="prompt-category"
                list="prompt-category-options"
                value={draft.category}
                placeholder={copy.newCategory}
                onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
              />
              <datalist id="prompt-category-options">
                {api.categories.map((c) => <option key={c} value={c} />)}
              </datalist>
              {problemLine && <PromptsNote $tone="error">{problemLine}</PromptsNote>}
              {workspaceNote && (
                <PromptsNote $tone="error" data-prompts-workspace-note="">{workspaceNote}</PromptsNote>
              )}
            </PromptEditorScroll>
            {/* An inline confirm REPLACES the strip — never a modal over a
                modal — and carries the real limit in a sentence. A
                confirmation that overstates what it undoes is how trust goes. */}
            {confirmWorkspace ? (
              <>
                <PromptsNote data-prompts-workspace-confirm={confirmWorkspace}>
                  {confirmWorkspace === 'share' ? copy.shareConfirm : copy.unshareConfirm}
                </PromptsNote>
                <PromptsFooter>
                  <PromptsFooterButton
                    type="button"
                    $primary
                    disabled={busy}
                    data-prompts-workspace-confirm-yes=""
                    onClick={() => {
                      if (!target) return;
                      if (confirmWorkspace === 'share') {
                        void runWorkspace(() => api.share(target.id), { closeAfter: true });
                      } else if (targetShelf === 'team') {
                        void runWorkspace(() => api.deleteShared(target.id), { closeAfter: true });
                      } else {
                        void runWorkspace(() => api.unshare(target.id), { closeAfter: true });
                      }
                    }}
                  >
                    {busy ? copy.sharing : copy.deleteYes}
                  </PromptsFooterButton>
                  <PromptsFooterButton type="button" onClick={() => setConfirmWorkspace(null)}>
                    {copy.deleteNo}
                  </PromptsFooterButton>
                </PromptsFooter>
              </>
            ) : (
              <PromptsFooter>
                <PromptsFooterButton type="button" $primary disabled={busy} onClick={() => void save()}>
                  {busy ? copy.saving : copy.save}
                </PromptsFooterButton>
                <PromptsFooterButton type="button" onClick={() => { setBody('list'); setProblem(null); }}>
                  {copy.cancel}
                </PromptsFooterButton>

                {/* One of YOURS: publish it, push your changes, or take it back
                    out. The push is offered only when there is drift to push —
                    a button that would be a no-op teaches people to ignore it. */}
                {mineTarget && !mineTarget.sharedAs && api.canPublish && (
                  <PromptsFooterButton
                    type="button"
                    data-prompts-share=""
                    onClick={() => setConfirmWorkspace('share')}
                  >
                    {copy.shareWithTeam}
                  </PromptsFooterButton>
                )}
                {mineTarget?.sharedAs && hasUnpublishedChanges(mineTarget) && (
                  <PromptsFooterButton
                    type="button"
                    disabled={busy}
                    data-prompts-republish=""
                    onClick={() => void runWorkspace(() => api.republish(mineTarget.id), { closeAfter: true })}
                  >
                    {copy.publishChanges}
                  </PromptsFooterButton>
                )}
                {mineTarget?.sharedAs && (
                  <PromptsFooterButton
                    type="button"
                    data-prompts-unshare=""
                    onClick={() => setConfirmWorkspace('unshare')}
                  >
                    {copy.unshareFromTeam}
                  </PromptsFooterButton>
                )}

                {/* The WORKSPACE's copy: removing it is the only destructive
                    thing on offer, and it never touches the author's own. */}
                {target && targetShelf === 'team' && (
                  <PromptsFooterButton
                    type="button"
                    data-prompts-remove-shared=""
                    onClick={() => setConfirmWorkspace('unshare')}
                  >
                    {copy.removeFromTeam}
                  </PromptsFooterButton>
                )}

                {mineTarget && (
                  confirmDelete === mineTarget.id ? (
                    <>
                      <PromptsFooterButton
                        type="button"
                        data-prompt-delete-confirm=""
                        onClick={() => { void api.remove(mineTarget.id); setBody('list'); setConfirmDelete(null); }}
                      >
                        {copy.deleteYes}
                      </PromptsFooterButton>
                      <PromptsFooterButton type="button" onClick={() => setConfirmDelete(null)}>
                        {copy.deleteNo}
                      </PromptsFooterButton>
                    </>
                  ) : (
                    <PromptsFooterButton type="button" onClick={() => setConfirmDelete(mineTarget.id)}>
                      {copy.deletePrompt}
                    </PromptsFooterButton>
                  )
                )}
              </PromptsFooter>
            )}
          </>
        )}
      </PromptsSurface>
    </>
  );

  return createPortal(surface, document.body);
};

export default SavedPromptsPanel;
